import { useCallback, useState } from "react";

// History state type
type HistoryState = {
    html: string;
    timestamp: number;
};

// History management hook
const useHistoryManager = (initialState: string) => {
    const [history, setHistory] = useState<HistoryState[]>([{ html: initialState, timestamp: Date.now() }]);
    const [currentIndex, setCurrentIndex] = useState(0);

    const addToHistory = useCallback((newState: string) => {
    // Don't add if it's the same as current state
    if (history[currentIndex]?.html === newState) {
        return;
    }
    
    const newHistoryState = { html: newState, timestamp: Date.now() };
    setHistory(prev => {
        const newHistory = prev.slice(0, currentIndex + 1);
        newHistory.push(newHistoryState);
        return newHistory.slice(-50); // Keep only last 50 states
    });
    setCurrentIndex(prev => Math.min(prev + 1, 49));
    }, [currentIndex, history]);

    const undo = useCallback(() => {
    if (currentIndex > 0) {
        const newIndex = currentIndex - 1;
        setCurrentIndex(newIndex);
        return history[newIndex]?.html || '';
    }
    return history[currentIndex]?.html || '';
    }, [currentIndex, history]);

    const redo = useCallback(() => {
    if (currentIndex < history.length - 1) {
        const newIndex = currentIndex + 1;
        setCurrentIndex(newIndex);
        return history[newIndex]?.html || '';
    }
    return history[currentIndex]?.html || '';
    }, [currentIndex, history]);

    const canUndo = currentIndex > 0;
    const canRedo = currentIndex < history.length - 1;

    return {
    currentState: history[currentIndex]?.html || '',
    addToHistory,
    undo,
    redo,
    canUndo,
    canRedo,
    historyLength: history.length,
    currentIndex,
    };
};

export default useHistoryManager;